import type { ReactNode } from 'react';
import { t } from '../i18n/idioma';
import { NICK, nickFill, nomeComMarca } from '../look/nickColor';
import { MenuRadial, type BotaoRadial } from './MenuRadial';

/** A distância de negociar no servidor (`TradeLimits.Chebyshev`). */
export const ALCANCE_TROCA = 2;
/** O padrão de `duelo.convite.alcance` (Configurações › Duelo); o servidor confere com o valor de lá. */
export const ALCANCE_DUELO = 12;

interface Props {
  name: string;
  /** 0 jogador, 1 GM, 2 admin. GM e admin: só mensagem e inspecionar. */
  cargo?: number;
  x: number;
  y: number;
  /** Notoriedade do fio (0 inocente, 1 criminoso, 2 assassino, 3 duelista) e a relação (1 guilda, 2 inimigo, 3 grupo). */
  notoriety?: number;
  rel?: number;
  /** Casas até ele (Chebyshev), na hora do clique. */
  dist?: number;
  alreadyFriend: boolean;
  canInvite?: boolean;
  targetHasGuild?: boolean;
  /** Ele está no seu grupo, e você pode expulsar (é o líder). */
  noGrupo?: boolean;
  canKick?: boolean;
  onTell(): void;
  onInspect?(): void;
  onAdd(): void;
  onRemoveFriend?(): void;
  onTrade(): void;
  onDuel?(): void;
  /** Fora de grupo, qualquer um convida; em grupo, só o líder. */
  canParty?: boolean;
  onParty?(): void;
  onKick?(): void;
  onInvite?(): void;
  onReport(): void;
  onClose(): void;
}

const svg = (d: ReactNode) => <svg viewBox="0 0 16 16" aria-hidden="true">{d}</svg>;
const ICONE = {
  msg: svg(<path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />),
  troca: svg(<path d="M3 5h9l-2.5-2.5M13 11H4l2.5 2.5" />),
  grupo: svg(<><circle cx="5" cy="6" r="2" /><circle cx="11" cy="6" r="2" /><path d="M1.5 13c.4-2 1.8-3 3.5-3s3.1 1 3.5 3M7.5 13c.4-2 1.8-3 3.5-3s3.1 1 3.5 3" /></>),
  expulsar: svg(<><circle cx="6" cy="6" r="2" /><path d="M2 13c.4-2 1.8-3 4-3s3.6 1 4 3M11 6.5h4" /></>),
  duelo: svg(<path d="M3 13l7-7M10 3h3v3M13 13L6 6M3 3h3v3" />),
  amigo: svg(<><circle cx="6" cy="5" r="2.5" /><path d="M1.5 13c.5-2.5 2.3-4 4.5-4s4 1.5 4.5 4" /><path d="M12.5 5v4M10.5 7h4" /></>),
  amigoTira: svg(<><circle cx="6" cy="5" r="2.5" /><path d="M1.5 13c.5-2.5 2.3-4 4.5-4s4 1.5 4.5 4" /><path d="M10.5 7h4" /></>),
  inspecionar: svg(<><path d="M10 10l4 4" /><circle cx="7" cy="7" r="4.5" /></>),
  guilda: svg(<><path d="M3 2.5h10v6c0 3-2.5 4.5-5 5.5-2.5-1-5-2.5-5-5.5z" /><path d="M8 5v5M5.5 7.5h5" /></>),
  denunciar: svg(<path d="M3 14V2.5M3 3h9l-2 3 2 3H3" />)
};

/**
 * O menu do jogador (botão direito), radial em volta do clique (docs/prototipos/menu-jogador-prototipo.html, opção C,
 * Renato 03/11/2026). O nome vai em cima, na cor do nick; as ações em anel; o que não vale agora fica apagado e diz o
 * motivo no meio do anel; o "Denunciar" é o último do anel, em vermelho, e abre a janela de denúncia como antes.
 */
export function PlayerMenu({
  name, cargo = 0, x, y, notoriety = 0, rel = 0, dist, alreadyFriend, canInvite, targetHasGuild, noGrupo = false, canKick = false,
  canParty, onTell, onInspect, onAdd, onRemoveFriend, onTrade, onDuel, onParty, onKick, onInvite, onReport, onClose
}: Props) {
  const staff = cargo > 0;
  const longe = (alcance: number) => dist !== undefined && dist > alcance;
  const botoes: BotaoRadial[] = [
    { id: 'msg', rotulo: t('menu.jogador.mensagem'), icone: ICONE.msg, onClick: onTell }
  ];
  if (!staff) {
    botoes.push({
      id: 'troca', rotulo: t('troca.negociar'), icone: ICONE.troca, onClick: onTrade,
      motivo: longe(ALCANCE_TROCA) ? t('menu.jogador.chegue', { n: ALCANCE_TROCA }) : ''
    });
    if (noGrupo && canKick && onKick) botoes.push({ id: 'expulsar', rotulo: t('menu.jogador.expulsar'), icone: ICONE.expulsar, onClick: onKick });
    else if (!noGrupo && canParty && onParty) botoes.push({ id: 'grupo', rotulo: t('menu.jogador.grupo'), icone: ICONE.grupo, onClick: onParty });
    if (onDuel) botoes.push({
      id: 'duelo', rotulo: t('duelo.menu'), icone: ICONE.duelo, onClick: onDuel,
      motivo: longe(ALCANCE_DUELO) ? t('menu.jogador.chegue', { n: ALCANCE_DUELO }) : ''
    });
    botoes.push(alreadyFriend
      ? { id: 'amigo', rotulo: t('menu.jogador.removerAmigo'), icone: ICONE.amigoTira, onClick: onRemoveFriend, motivo: onRemoveFriend ? '' : t('amigo.jaEhAmigo') }
      : { id: 'amigo', rotulo: t('menu.jogador.amigo'), icone: ICONE.amigo, onClick: onAdd });
  }
  if (onInspect) botoes.push({ id: 'inspecionar', rotulo: t('inspecao.menu'), icone: ICONE.inspecionar, onClick: onInspect });
  if (!staff && canInvite) botoes.push({
    id: 'guilda', rotulo: t('menu.jogador.guilda'), icone: ICONE.guilda, onClick: onInvite,
    motivo: targetHasGuild ? t('guilda.jaTemGuilda') : ''
  });
  // Denunciar fecha o anel, em vermelho (Renato, 03/11): abre a janela de denúncia de sempre.
  if (!staff) botoes.push({ id: 'denunciar', rotulo: t('menu.jogador.denunciar'), icone: ICONE.denunciar, onClick: onReport, perigo: true });

  const cor = staff ? NICK.staff : nickFill({ notoriety, inParty: rel === 3, guildAlly: rel === 1, enemyGuild: rel === 2 });
  const nota = notoriety === 1 ? t('menu.jogador.criminoso') : notoriety === 2 ? t('menu.jogador.assassino') : '';
  const sub = [nota, noGrupo ? t('menu.jogador.noGrupo') : '', alreadyFriend && !staff ? t('menu.jogador.ehAmigo') : ''].filter(Boolean).join(' · ');

  return <MenuRadial titulo={nomeComMarca(name, cargo)} sub={sub || undefined} cor={cor} x={x} y={y} botoes={botoes} onClose={onClose} />;
}
